import { getElColorPrimary, EL_COLOR_PRIMARY_FALLBACK } from '@/utils/theme-color'

export const ECHARTS_THEME_REFRESH_EVENT = 'echarts-theme-refresh'

/** 读取 CSS 变量为 ECharts 可用的颜色字符串（canvas 不支持 var()） */
export function readCssVar (name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

export function getCssColorVar (name: string, fallback: string): string {
  return readCssVar(name, fallback)
}

/** ECharts 默认调色盘：第二项为主色，随 dashboard 双主题变化 */
export function getEchartsColorPalette (): string[] {
  const primary = getElColorPrimary()
  return [
    '#C50000',
    primary,
    '#F5BA18',
    '#1BA041',
    '#2BA471',
    '#9a60b4',
    '#ea7ccc',
    '#73c0de',
    '#0DC0F8',
  ]
}

/** 当前是否深色模式（html.app-dark） */
export function isAppDark (): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('app-dark')
}

/**
 * 饼图主色系（多扇区）：主色 + Element 浅色阶梯。
 * 深色模式下 light-3~9 阶梯为「深底渐变」（mix 深背景），饼图扇区之间无法分辨，
 * 改取亮端阶梯（primary / dark-2 / light-1~4）保证扇区层次。
 */
export function getChartPiePrimaryPalette (count = 10): string[] {
  const steps = isAppDark()
    ? [
        readCssVar('--el-color-primary-dark-2', getElColorPrimary()),
        readCssVar('--el-color-primary', getElColorPrimary()),
        readCssVar('--el-color-primary-light-1', '#66b1ff'),
        readCssVar('--el-color-primary-light-2', '#9fceff'),
        readCssVar('--el-color-primary-light-3', '#c6e2ff'),
        readCssVar('--el-color-primary-light-4', '#d9ecff'),
      ]
    : [
        readCssVar('--el-color-primary', getElColorPrimary()),
        readCssVar('--el-color-primary-light-3', '#66b1ff'),
        readCssVar('--el-color-primary-light-5', '#9fceff'),
        readCssVar('--el-color-primary-light-7', '#c6e2ff'),
        readCssVar('--el-color-primary-light-8', '#d9ecff'),
        readCssVar('--el-color-primary-light-9', '#ecf5ff'),
        readCssVar('--el-color-primary-dark-2', EL_COLOR_PRIMARY_FALLBACK),
      ]
  const out: string[] = []
  for (let i = 0; i < count; i++) {
    out.push(steps[i % steps.length])
  }
  return out
}

/** 饼图：主色 + 固定辅助色（部门/软件分类等） */
export function getChartPieMixedPalette (): string[] {
  const primary = getElColorPrimary()
  return [
    primary,
    '#1BA041',
    readCssVar('--el-color-primary-light-3', '#66b1ff'),
    '#FFC107',
    '#FF9800',
    '#E91E63',
    '#9C27B0',
    '#00BCD4',
    '#795548',
    '#607D8B',
  ]
}

/** 安全事件饼图专用调色盘：6 色（低危/中危/高危/绿/紫/青），主色占位随主题变化 */
export function getSecurityEventPiePalette (count = 6): string[] {
  const base = ['#E54545', '#FB8F10', '#0456AD', '#31C872', '#7B61FF', '#00C9C9']
  base[2] = readCssVar('--el-color-primary', '#0456AD')
  const out: string[] = []
  for (let i = 0; i < count; i++) {
    out.push(base[i % base.length])
  }
  return out
}

export function dispatchEchartsThemeRefresh (): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ECHARTS_THEME_REFRESH_EVENT))
}

export function onEchartsThemeRefresh (handler: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(ECHARTS_THEME_REFRESH_EVENT, handler)
  return () => window.removeEventListener(ECHARTS_THEME_REFRESH_EVENT, handler)
}
